import { WebIO } from '@gltf-transform/core';
import {
  dedup,
  normals,
  prune,
  simplify,
  weld,
} from '@gltf-transform/functions';
import { MeshoptSimplifier } from 'meshoptimizer';

let ioPromise: Promise<WebIO> | null = null;

async function getIO(): Promise<WebIO> {
  if (!ioPromise) {
    ioPromise = (async () => {
      await MeshoptSimplifier.ready;
      return new WebIO();
    })();
  }
  return ioPromise;
}

// STL-derived meshes have per-face normals, so every triangle vertex is "split"
// on the NORMAL attribute and weld() can't merge coincident positions. Dropping
// NORMAL (and the derived TANGENT) lets weld do its job; normals() regenerates
// them from geometry after simplification.
function stripNormals(doc: Awaited<ReturnType<WebIO['readBinary']>>): void {
  const drop = ['NORMAL', 'TANGENT'];
  for (const mesh of doc.getRoot().listMeshes()) {
    for (const prim of mesh.listPrimitives()) {
      for (const semantic of drop) {
        const accessor = prim.getAttribute(semantic);
        if (!accessor) {
          continue;
        }
        prim.setAttribute(semantic, null);
        if (accessor.listParents().length === 1) {
          accessor.dispose();
        }
      }
    }
  }
}

export async function simplifyGlb(file: File, ratio: number): Promise<File> {
  const io = await getIO();
  const input = new Uint8Array(await file.arrayBuffer());
  const doc = await io.readBinary(input);
  stripNormals(doc);
  await doc.transform(
    weld({ overwrite: true }),
    simplify({
      simplifier: MeshoptSimplifier,
      ratio,
      error: 1,
      lockBorder: false,
    }),
    normals({ overwrite: true }),
    dedup(),
    prune(),
  );
  const output = await io.writeBinary(doc);
  return new File([output as BlobPart], file.name, {
    type: 'model/gltf-binary',
  });
}
